import { css } from '@emotion/react';

import type { RehearsalDebugData } from '@/hooks/use-rehearsal-debug';
import { darkTheme } from '@/theme/dark-theme';
import { fontFamilies } from '@/theme/tokens';

export interface DebugConsoleProps {
  data: RehearsalDebugData;
  onClose: () => void;
}

function StatusDot({ listening, hasErrors }: { listening: boolean; hasErrors: boolean }) {
  const color = hasErrors ? darkTheme.error : listening ? darkTheme.success : darkTheme.textMuted;
  return (
    <span
      data-testid="status-dot"
      css={css`
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${color};
        margin-right: 6px;
      `}
    />
  );
}

function timeSince(ms: number | null): string {
  if (ms === null) return 'never';
  const ago = Math.round((Date.now() - ms) / 1000);
  return `${ago}s ago`;
}

export function DebugConsole({ data, onClose }: DebugConsoleProps) {
  const a = data.aligner;

  return (
    <div
      data-testid="debug-console"
      css={css`
        flex-shrink: 0;
        background: rgba(31, 22, 51, 0.94);
        backdrop-filter: blur(4px);
        border-bottom: 1px solid ${darkTheme.border};
        z-index: 1;
        font-family: ${fontFamilies.mono};
        font-size: 11px;
        line-height: 1.5;
        color: ${darkTheme.text};
        padding: 6px 10px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          padding-bottom: 4px;
          border-bottom: 1px solid ${darkTheme.border};
        `}
      >
        <span
          css={css`
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: ${darkTheme.textSecondary};
          `}
        >
          Debug Console
        </span>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          <button
            type="button"
            onClick={data.downloadLog}
            aria-label="Download debug log"
            css={css`
              background: none;
              border: none;
              color: ${darkTheme.textSecondary};
              cursor: pointer;
              font-size: 12px;
              padding: 0 2px;
              line-height: 1;

              &:hover {
                color: ${darkTheme.text};
              }
            `}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close debug console"
            css={css`
              background: none;
              border: none;
              color: ${darkTheme.textSecondary};
              cursor: pointer;
              font-size: 14px;
              padding: 0 2px;
              line-height: 1;

              &:hover {
                color: ${darkTheme.text};
              }
            `}
          >
            &times;
          </button>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: 0 12px;
          max-height: 140px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: ${darkTheme.border} transparent;
        `}
      >
        {/* Recognition */}
        <Section title="Recognition">
          <Row label="Status">
            <StatusDot listening={data.isListening} hasErrors={data.errors.length > 0} />
            {data.isListening ? 'Listening' : 'Not listening'}
          </Row>
          <Row label="Last event">{timeSince(data.lastEventMs)}</Row>
          <Row label="Restarts">{data.restartCount}</Row>
          <TextBlock label="Interim" italic>
            {data.interimText || '—'}
          </TextBlock>
          <TextBlock label="Final">{data.finalText || '—'}</TextBlock>
        </Section>

        {/* Alignment */}
        <Section title="Alignment">
          <Row label="Cursor">{a ? `${a.cursorPosition} / ${a.scriptWordCount}` : '—'}</Row>
          <Row label="Section">
            {a ? `${a.sectionIndex + 1} at ${Math.round(a.sectionProgress * 100)}%` : '—'}
          </Row>
          <TextBlock label="Buffer">
            {a && a.transcriptBuffer.length > 0 ? a.transcriptBuffer.join(' ') : '—'}
          </TextBlock>
        </Section>

        {/* Pace */}
        <Section title="Pace">
          <Row label="Avg WPM">
            {a?.avgWpm !== null && a?.avgWpm !== undefined ? Math.round(a.avgWpm) : '—'}
          </Row>
          <Row label="Anchor">
            {a?.anchor !== null && a?.anchor !== undefined ? Math.round(a.anchor) : '—'}
          </Row>
          <Row label="Section WPMs">
            {a && a.sectionWpms.length > 0
              ? a.sectionWpms.map((w) => Math.round(w)).join(', ')
              : '—'}
          </Row>
        </Section>

        {/* Errors */}
        {data.errors.length > 0 && (
          <Section title="Errors">
            {data.errors.map((err, i) => (
              <div
                key={i}
                css={css`
                  color: ${darkTheme.error};
                  font-size: 10px;
                  margin-bottom: 2px;
                `}
              >
                [{err.type}] {err.message}
              </div>
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      css={css`
        flex: 1;
        min-width: 200px;
        padding-right: 12px;
        border-right: 1px solid ${darkTheme.border}33;

        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      `}
    >
      <div
        css={css`
          font-weight: 600;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${darkTheme.primary};
          margin-bottom: 3px;
        `}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1px;
      `}
    >
      <span
        css={css`
          color: ${darkTheme.textSecondary};
        `}
      >
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}

function TextBlock({
  label,
  italic,
  children,
}: {
  label: string;
  italic?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      css={css`
        margin-bottom: 3px;
      `}
    >
      <span
        css={css`
          color: ${darkTheme.textSecondary};
        `}
      >
        {label}:
      </span>
      <div
        css={css`
          color: ${italic ? darkTheme.textSecondary : darkTheme.text};
          font-style: ${italic ? 'italic' : 'normal'};
          word-break: break-word;
          margin-top: 1px;
          padding: 2px 4px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
          max-height: 60px;
          overflow-y: auto;
        `}
      >
        {children}
      </div>
    </div>
  );
}
